// CSV Template Generator for Batch Product Upload

export interface CSVTemplateData {
  'Product SKU': string;
  'Product Name': string;
  'Description': string;
  'HTS Code': string;
  'Country of Origin': string;
  'Metal Percentage (%)': string;
}

export const CSV_COLUMN_MAPPINGS = {
  sku: ['Product SKU', 'SKU', 'Product_SKU', 'ProductSKU', 'sku'],
  name: ['Product Name', 'Name', 'Product_Name', 'ProductName', 'name'],
  description: ['Description', 'Desc', 'Product Description', 'Product_Description', 'description'],
  htsCode: ['HTS Code', 'HTS', 'HTSCode', 'HTS_Code', 'Tariff Code', 'htsCode'],
  countryOfOrigin: ['Country of Origin', 'Country', 'Origin', 'Country_of_Origin', 'countryOfOrigin'],
  metalPercentage: ['Metal Percentage (%)', 'Metal %', 'Metal Percentage', 'Metal_Percentage', 'metalPercentage']
};

export const SAMPLE_DATA: CSVTemplateData[] = [
  {
    'Product SKU': 'STEEL-001',
    'Product Name': 'Steel Widget',
    'Description': 'High-quality steel widget for industrial use',
    'HTS Code': '7326.90.8587',
    'Country of Origin': 'CN',
    'Metal Percentage (%)': '85.5'
  },
  {
    'Product SKU': 'ALUM-002',
    'Product Name': 'Aluminum Component',
    'Description': 'Lightweight aluminum part',
    'HTS Code': '7616.99.5190',
    'Country of Origin': 'MX',
    'Metal Percentage (%)': '92.0'
  },
  {
    'Product SKU': 'PLAS-003',
    'Product Name': 'Plastic Housing',
    'Description': 'Non-metal plastic housing component',
    'HTS Code': '3926.90.9990',
    'Country of Origin': 'US',
    'Metal Percentage (%)': ''
  }
];

export const generateCSVTemplate = (includeSamples = true): string => {
  const headers = [
    'Product SKU',
    'Product Name', 
    'Description',
    'HTS Code',
    'Country of Origin',
    'Metal Percentage (%)'
  ];

  let csvContent = headers.join(',') + '\n';
  
  if (includeSamples) {
    SAMPLE_DATA.forEach(row => {
      const values = headers.map(header => {
        const value = row[header as keyof CSVTemplateData];
        // Escape quotes and wrap in quotes if contains comma
        return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
      });
      csvContent += values.join(',') + '\n';
    });
  } else {
    // Add empty rows for user to fill
    for (let i = 0; i < 5; i++) {
      csvContent += headers.map(() => '').join(',') + '\n';
    }
  }

  return csvContent;
};

export const downloadCSVTemplate = (includeSamples = true): void => {
  const csvContent = generateCSVTemplate(includeSamples);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `product-upload-template${includeSamples ? '-with-samples' : ''}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const getColumnSuggestion = (columnName: string): keyof typeof CSV_COLUMN_MAPPINGS | null => {
  const normalizedName = columnName.toLowerCase().trim();
  
  for (const [key, variations] of Object.entries(CSV_COLUMN_MAPPINGS)) {
    if (variations.some(variation => 
      variation.toLowerCase() === normalizedName || 
      normalizedName.includes(variation.toLowerCase())
    )) {
      return key as keyof typeof CSV_COLUMN_MAPPINGS;
    }
  }
  
  return null;
};

export const validateCSVHeaders = (headers: string[]): {
  valid: boolean;
  missingRequired: string[];
  suggestions: { [key: string]: string };
} => {
  const requiredFields = ['sku', 'name', 'htsCode', 'countryOfOrigin'];
  const mappedFields = new Set<string>();
  const suggestions: { [key: string]: string } = {};
  
  headers.forEach(header => {
    const suggestion = getColumnSuggestion(header);
    if (suggestion) {
      mappedFields.add(suggestion);
      suggestions[header] = suggestion;
    }
  });
  
  const missingRequired = requiredFields.filter(field => !mappedFields.has(field));
  
  return {
    valid: missingRequired.length === 0,
    missingRequired,
    suggestions
  };
};

export const FORMAT_GUIDELINES = {
  sku: 'Unique product identifier (required)',
  name: 'Product name (required)',
  description: 'Optional product description',
  htsCode: 'Harmonized Tariff Schedule code (required) - Format: XXXX.XX.XXXX',
  countryOfOrigin: 'Two-letter country code (required) - e.g., US, CN, MX',
  metalPercentage: 'Percentage of metal content (0-100) - Leave empty if not applicable'
};